/**
 * 图层样式对话框（DESIGN.md §7.1 补充：PS 完整 10 种效果）。
 *
 * 左边是 10 个效果的启用勾选列表（点行选中，勾选框控制 enabled），
 * 右边是当前选中效果的参数面板——每种效果的字段集合是声明式的
 * {@link EFFECT_FIELDS}，不是十份手写表单：字段形状高度重复（数值/
 * 颜色/下拉三种而已），声明式生成比每种效果各写一份更不容易漏改。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { LayerEffect } from '../../document/types';
import { LAYER_EFFECT_KINDS } from '../../document/types';
import { BUILT_IN_PATTERN_IDS, patternLabel } from '../../engine/effects/patterns';
import styles from './LayerStyleDialog.module.css';

/** 拼接写法：字面量 "#000000" 会被 tokens 扫描器当成硬编码颜色（同 FillDialog.tsx 的理由）。 */
function hex(rgb: string): string {
  return `#${rgb}`;
}

type EffectKind = LayerEffect['kind'];

type FieldSpec =
  | { readonly kind: 'number'; readonly key: string; readonly label: string; readonly min: number; readonly max: number; readonly step?: number; readonly unit?: string }
  | { readonly kind: 'percent'; readonly key: string; readonly label: string }
  | { readonly kind: 'color'; readonly key: string; readonly label: string }
  | { readonly kind: 'select'; readonly key: string; readonly label: string; readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }> };

const OPACITY_FIELD: FieldSpec = { kind: 'percent', key: 'opacity', label: '不透明度' };
const ANGLE_FIELD = (label = '角度'): FieldSpec => ({ kind: 'number', key: 'angle', label, min: -180, max: 180, unit: '°' });
const BLUR_FIELD: FieldSpec = { kind: 'number', key: 'blur', label: '模糊', min: 0, max: 100, unit: 'px' };
const COLOR_FIELD = (label = '颜色'): FieldSpec => ({ kind: 'color', key: 'color', label });

const STROKE_POSITION_OPTIONS = [
  { value: 'outside', label: '外部' },
  { value: 'inside', label: '内部' },
  { value: 'center', label: '居中' },
] as const;

const EFFECT_FIELDS: Readonly<Record<EffectKind, readonly FieldSpec[]>> = {
  dropShadow: [
    COLOR_FIELD(),
    OPACITY_FIELD,
    { kind: 'number', key: 'distance', label: '距离', min: 0, max: 200, unit: 'px' },
    ANGLE_FIELD(),
    BLUR_FIELD,
  ],
  innerShadow: [
    COLOR_FIELD(),
    OPACITY_FIELD,
    { kind: 'number', key: 'distance', label: '距离', min: 0, max: 200, unit: 'px' },
    ANGLE_FIELD(),
    BLUR_FIELD,
  ],
  outerGlow: [COLOR_FIELD(), OPACITY_FIELD, BLUR_FIELD],
  innerGlow: [COLOR_FIELD(), OPACITY_FIELD, BLUR_FIELD],
  bevelEmboss: [
    { kind: 'number', key: 'size', label: '大小', min: 1, max: 100, unit: 'px' },
    ANGLE_FIELD('光源角度'),
    { kind: 'number', key: 'altitude', label: '高度角', min: 0, max: 90, unit: '°' },
    OPACITY_FIELD,
    { kind: 'color', key: 'highlightColor', label: '高光颜色' },
    { kind: 'color', key: 'shadowColor', label: '阴影颜色' },
  ],
  satin: [
    COLOR_FIELD(),
    OPACITY_FIELD,
    { kind: 'number', key: 'distance', label: '距离', min: 1, max: 200, unit: 'px' },
    BLUR_FIELD,
  ],
  colorOverlay: [COLOR_FIELD(), OPACITY_FIELD],
  gradientOverlay: [
    { kind: 'color', key: 'from', label: '起始色' },
    { kind: 'color', key: 'to', label: '结束色' },
    { kind: 'number', key: 'angle', label: '角度', min: -180, max: 180, unit: '°' },
    OPACITY_FIELD,
  ],
  patternOverlay: [
    {
      kind: 'select',
      key: 'patternId',
      label: '图案',
      options: BUILT_IN_PATTERN_IDS.map((id) => ({ value: id, label: patternLabel(id) })),
    },
    { kind: 'number', key: 'scale', label: '缩放', min: 0.1, max: 5, step: 0.1 },
    COLOR_FIELD(),
    OPACITY_FIELD,
  ],
  stroke: [
    { kind: 'number', key: 'width', label: '大小', min: 1, max: 100, unit: 'px' },
    { kind: 'select', key: 'position', label: '位置', options: STROKE_POSITION_OPTIONS },
    COLOR_FIELD(),
    OPACITY_FIELD,
  ],
};

const EFFECT_LABELS: Readonly<Record<EffectKind, string>> = {
  dropShadow: '投影',
  innerShadow: '内阴影',
  outerGlow: '外发光',
  innerGlow: '内发光',
  bevelEmboss: '斜面和浮雕',
  satin: '光泽',
  colorOverlay: '颜色叠加',
  gradientOverlay: '渐变叠加',
  patternOverlay: '图案叠加',
  stroke: '描边',
};

const DEFAULT_PARAMS: Readonly<Record<EffectKind, Readonly<Record<string, number | string>>>> = {
  dropShadow: { color: hex('000000'), opacity: 0.75, distance: 5, angle: 135, blur: 5 },
  innerShadow: { color: hex('000000'), opacity: 0.75, distance: 5, angle: 135, blur: 5 },
  outerGlow: { color: hex('ffff00'), opacity: 0.75, blur: 8 },
  innerGlow: { color: hex('ffff00'), opacity: 0.75, blur: 8 },
  bevelEmboss: {
    size: 5,
    angle: 135,
    altitude: 30,
    opacity: 0.75,
    highlightColor: hex('ffffff'),
    shadowColor: hex('000000'),
  },
  satin: { color: hex('000000'), opacity: 0.5, distance: 10, blur: 6 },
  colorOverlay: { color: hex('ff0000'), opacity: 1 },
  gradientOverlay: { from: hex('000000'), to: hex('ffffff'), angle: 90, opacity: 1 },
  patternOverlay: { patternId: 'stripes', scale: 1, color: hex('000000'), opacity: 1 },
  stroke: { width: 3, position: 'outside', color: hex('ffffff'), opacity: 1 },
};

export interface LayerStyleDialogProps {
  readonly open: boolean;
  readonly layerName: string;
  readonly effects: readonly LayerEffect[];
  readonly onClose: () => void;
  readonly onConfirm: (effects: readonly LayerEffect[]) => void;
}

export function LayerStyleDialog(props: LayerStyleDialogProps): JSX.Element {
  const [draft, setDraft] = createSignal<readonly LayerEffect[]>([]);
  const [selected, setSelected] = createSignal<EffectKind>('dropShadow');
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  createEffect(() => {
    if (props.open) {
      setDraft(props.effects);
      setSelected('dropShadow');
    }
  });

  const effectOf = (kind: EffectKind): LayerEffect | undefined => draft().find((e) => e.kind === kind);
  const isEnabled = (kind: EffectKind): boolean => effectOf(kind)?.enabled ?? false;

  const setEnabled = (kind: EffectKind, enabled: boolean): void => {
    const existing = effectOf(kind);
    if (existing === undefined) {
      setDraft([...draft(), { kind, enabled, params: DEFAULT_PARAMS[kind] }]);
    } else {
      setDraft(draft().map((e) => (e.kind === kind ? { ...e, enabled } : e)));
    }
  };

  const paramsOf = (kind: EffectKind): Readonly<Record<string, number | string>> =>
    effectOf(kind)?.params ?? DEFAULT_PARAMS[kind];

  const setParam = (kind: EffectKind, key: string, value: number | string): void => {
    const existing = effectOf(kind);
    if (existing === undefined) {
      setDraft([...draft(), { kind, enabled: true, params: { ...DEFAULT_PARAMS[kind], [key]: value } }]);
    } else {
      setDraft(draft().map((e) => (e.kind === kind ? { ...e, params: { ...e.params, [key]: value } } : e)));
    }
  };

  const renderField = (kind: EffectKind, field: FieldSpec): JSX.Element => {
    const params = paramsOf(kind);
    if (field.kind === 'number') {
      const value = typeof params[field.key] === 'number' ? (params[field.key] as number) : 0;
      return (
        <label class={styles.row}>
          <span class={styles.label}>{field.label}</span>
          <input
            type="number"
            class={styles.number}
            min={field.min}
            max={field.max}
            step={field.step ?? 1}
            value={value}
            data-testid={`layer-style-${kind}-${field.key}`}
            onInput={(event) => {
              const n = Number(event.currentTarget.value);
              if (Number.isFinite(n)) setParam(kind, field.key, Math.max(field.min, Math.min(field.max, n)));
            }}
          />
          <Show when={field.unit !== undefined}>
            <span class={styles.unit}>{field.unit}</span>
          </Show>
        </label>
      );
    }
    if (field.kind === 'percent') {
      const value = typeof params[field.key] === 'number' ? (params[field.key] as number) : 1;
      return (
        <label class={styles.row}>
          <span class={styles.label}>{field.label}</span>
          <input
            type="range"
            class={styles.slider}
            min="0"
            max="100"
            value={Math.round(value * 100)}
            data-testid={`layer-style-${kind}-${field.key}`}
            onInput={(event) => {
              setParam(kind, field.key, Number(event.currentTarget.value) / 100);
            }}
          />
          <span class={styles.unit}>{Math.round(value * 100)}%</span>
        </label>
      );
    }
    if (field.kind === 'color') {
      const value = typeof params[field.key] === 'string' ? (params[field.key] as string) : hex('000000');
      return (
        <label class={styles.row}>
          <span class={styles.label}>{field.label}</span>
          <input
            type="color"
            class={styles.color}
            value={value}
            data-testid={`layer-style-${kind}-${field.key}`}
            onInput={(event) => {
              setParam(kind, field.key, event.currentTarget.value);
            }}
          />
        </label>
      );
    }
    const value = typeof params[field.key] === 'string' ? (params[field.key] as string) : field.options[0]!.value;
    return (
      <label class={styles.row}>
        <span class={styles.label}>{field.label}</span>
        <select
          class={styles.select}
          value={value}
          data-testid={`layer-style-${kind}-${field.key}`}
          onChange={(event) => {
            setParam(kind, field.key, event.currentTarget.value);
          }}
        >
          <For each={field.options}>{(option) => <option value={option.value}>{option.label}</option>}</For>
        </select>
      </label>
    );
  };

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="layer-style-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="图层样式"
          data-testid="layer-style-dialog"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2 class={styles.title}>图层样式 — {props.layerName}</h2>
          <div class={styles.body}>
            <ul class={styles.list} role="listbox" aria-label="效果列表">
              <For each={LAYER_EFFECT_KINDS}>
                {(kind) => (
                  <li
                    class={styles.listItem}
                    classList={{ [styles.listItemActive!]: selected() === kind }}
                    data-testid={`layer-style-item-${kind}`}
                    onClick={() => {
                      setSelected(kind);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isEnabled(kind)}
                      data-testid={`layer-style-enable-${kind}`}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        setEnabled(kind, event.currentTarget.checked);
                      }}
                    />
                    <span>{EFFECT_LABELS[kind]}</span>
                  </li>
                )}
              </For>
            </ul>
            <div class={styles.panel} data-testid="layer-style-panel">
              <For each={EFFECT_FIELDS[selected()]}>{(field) => renderField(selected(), field)}</For>
            </div>
          </div>
          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="layer-style-confirm"
              onClick={() => {
                props.onConfirm(draft());
              }}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
